import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import Navbar from '../components/Navbar';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './AdminReports.css';

function AdminReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await adminAPI.getReports();
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports data.');
    } finally {
      setLoading(false);
    }
  };

  // Build monthly signups chart data
  const buildSignupsData = () => {
    if (!reports?.signupsByMonth) return [];
    const monthMap = {};
    reports.signupsByMonth.forEach(({ month, subscriptionPlan, count }) => {
      const label = new Date(month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (!monthMap[label]) monthMap[label] = { month: label, free: 0, pro: 0, premium: 0, organization: 0 };
      monthMap[label][subscriptionPlan] = parseInt(count);
    });
    return Object.values(monthMap);
  };

  // Build monthly games chart data
  const buildGamesData = () => {
    if (!reports?.gamesByMonth) return [];
    return reports.gamesByMonth.map(({ month, count }) => ({
      month: new Date(month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      games: parseInt(count),
    }));
  };

  // Build analyst by tier data
  const buildAnalystData = () => {
    if (!reports?.analystByTier) return [];
    return reports.analystByTier.map(({ subscriptionPlan, totalMessages, userCount, avgMessages }) => ({
      plan: subscriptionPlan.charAt(0).toUpperCase() + subscriptionPlan.slice(1),
      totalMessages: parseInt(totalMessages) || 0,
      userCount: parseInt(userCount) || 0,
      avgMessages: parseFloat(avgMessages || 0).toFixed(1),
    }));
  };

  const signupsData = buildSignupsData();
  const gamesData = buildGamesData();
  const analystData = buildAnalystData();

  const PLAN_COLORS = {
    free: '#94a3b8',
    pro: '#3b82f6',
    premium: '#8b5cf6',
    organization: '#f59e0b',
  };

  return (
    <div className="admin-reports-wrapper">
      <Navbar />
      <div className="admin-reports-container">
        <div className="admin-reports-content">

          {/* Header */}
          <div className="reports-header">
            <div>
              <button className="reports-back-btn" onClick={() => navigate('/admin')}>← Admin Portal</button>
              <h1>Reports & Analytics</h1>
              <p>Platform metrics, trends, and business insights</p>
            </div>
            <button className="reports-refresh-btn" onClick={fetchReports}>↻ Refresh</button>
          </div>

          {loading && <div className="reports-loading">Loading reports...</div>}
          {error && <div className="reports-error">{error}</div>}

          {reports && (
            <>
              {/* ── Snapshot Cards ── */}
              <div className="reports-section-title">Snapshot</div>
              <div className="reports-cards-grid">
                <div className="reports-card">
                  <div className="reports-card-value">{reports.featureAdoption.totalActiveUsers}</div>
                  <div className="reports-card-label">Total Active Users</div>
                </div>
                <div className="reports-card">
                  <div className="reports-card-value">{reports.conversion.paidUsers}</div>
                  <div className="reports-card-label">Paid Users</div>
                </div>
                <div className="reports-card accent">
                  <div className="reports-card-value">{reports.conversion.conversionRate}%</div>
                  <div className="reports-card-label">Conversion Rate</div>
                </div>
                <div className="reports-card">
                  <div className="reports-card-value">{reports.avgGamesPerUser}</div>
                  <div className="reports-card-label">Avg Games / User</div>
                </div>
              </div>

              {/* ── Feature Adoption ── */}
              <div className="reports-section-title">Feature Adoption</div>
              <div className="reports-adoption-grid">
                {[
                  { label: 'Tracked a Game', pct: reports.featureAdoption.pctWithGames, count: reports.featureAdoption.usersWithGames },
                  { label: 'Created a BragCard', pct: reports.featureAdoption.pctWithBragCards, count: reports.featureAdoption.usersWithBragCards },
                  { label: 'Used Ask the Analyst', pct: reports.featureAdoption.pctWithAnalyst, count: reports.featureAdoption.usersWithAnalyst },
                ].map(({ label, pct, count }) => (
                  <div key={label} className="reports-adoption-card">
                    <div className="adoption-label">{label}</div>
                    <div className="adoption-bar-wrap">
                      <div className="adoption-bar" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <div className="adoption-stats">{pct}% <span>({count} users)</span></div>
                  </div>
                ))}
              </div>

              {/* ── Signups by Month ── */}
              <div className="reports-section-title">New Signups by Month</div>
              <div className="reports-chart-card">
                {signupsData.length === 0 ? (
                  <div className="reports-empty">No signup data available.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={signupsData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="free" name="Free" stackId="a" fill={PLAN_COLORS.free} />
                      <Bar dataKey="pro" name="Pro" stackId="a" fill={PLAN_COLORS.pro} />
                      <Bar dataKey="premium" name="Premium" stackId="a" fill={PLAN_COLORS.premium} />
                      <Bar dataKey="organization" name="Org" stackId="a" fill={PLAN_COLORS.organization} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* ── Games Tracked by Month ── */}
              <div className="reports-section-title">Games Tracked by Month</div>
              <div className="reports-chart-card">
                {gamesData.length === 0 ? (
                  <div className="reports-empty">No games data available.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={gamesData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="games" name="Games Tracked" stroke="#c0001a" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* ── Ask the Analyst by Tier ── */}
              <div className="reports-section-title">Ask the Analyst Usage by Tier</div>
              <div className="reports-chart-card">
                {analystData.length === 0 ? (
                  <div className="reports-empty">No analyst data available.</div>
                ) : (
                  <table className="reports-table">
                    <thead>
                      <tr>
                        <th>Plan</th>
                        <th>Users</th>
                        <th>Total Messages</th>
                        <th>Avg Messages / User</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analystData.map(({ plan, totalMessages, userCount, avgMessages }) => (
                        <tr key={plan}>
                          <td><span className={`plan-badge plan-${plan.toLowerCase()}`}>{plan}</span></td>
                          <td>{userCount}</td>
                          <td>{totalMessages}</td>
                          <td>{avgMessages}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

export default AdminReports;
